import { useState, useRef, useCallback } from 'react';

type RecorderStatus = 'idle' | 'recording' | 'stopped';

interface UseRealtimePCMRecorderOptions {
  sampleRate?: number;
  channelCount?: number;
  dataInterval?: number;
  onData?: (pcmData: ArrayBuffer) => void;
  onStop?: (finalData: ArrayBuffer) => void;
  onError?: (error: Error) => void;
}

export const useRealtimePCMRecorder = ({
  sampleRate = 16000,
  channelCount = 1,
  dataInterval = 1000,
  onData,
  onStop,
  onError
}: UseRealtimePCMRecorderOptions = {}) => {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const pcmDataRef = useRef<Float32Array[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const convertFloat32ToPCM16 = (float32Array: Float32Array): ArrayBuffer => {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    let offset = 0;
    
    for (let i = 0; i < float32Array.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
    
    return buffer;
  };

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setStatus('recording');
      
      // 获取麦克风权限
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate,
          channelCount,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      streamRef.current = stream;
      pcmDataRef.current = [];
      
      // 创建 AudioContext
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate
      });
      audioContextRef.current = audioContext;
      
      // 创建音频源节点
      const sourceNode = audioContext.createMediaStreamSource(stream);
      sourceNodeRef.current = sourceNode;
      
      // 创建处理器节点
      const bufferSize = 4096;
      const processorNode = audioContext.createScriptProcessor(bufferSize, channelCount, channelCount);
      processorNodeRef.current = processorNode;
      
      // 处理音频数据
      processorNode.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer;
        const channelData = inputBuffer.getChannelData(0); // 获取第一个声道
        
        // 存储数据
        pcmDataRef.current.push(new Float32Array(channelData));
      };
      
      // 连接节点
      sourceNode.connect(processorNode);
      processorNode.connect(audioContext.destination);
      
      // 设置定时发送数据
      intervalRef.current = setInterval(() => {
        if (pcmDataRef.current.length > 0) {
          // 合并所有数据
          const totalLength = pcmDataRef.current.reduce((sum, arr) => sum + arr.length, 0);
          const mergedData = new Float32Array(totalLength);
          let offset = 0;
          
          for (const chunk of pcmDataRef.current) {
            mergedData.set(chunk, offset);
            offset += chunk.length;
          }
          
          // 转换为 PCM16 格式
          const pcmBuffer = convertFloat32ToPCM16(mergedData);
          
          // 发送数据
          if (onData) {
            onData(pcmBuffer);
          }
          
          // 清空缓存
          pcmDataRef.current = [];
        }
      }, dataInterval);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setStatus('idle');
      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMessage));
      }
    }
  }, [sampleRate, channelCount, dataInterval, onData, onError]);

  const stopRecording = useCallback(() => {
    try {
      // 停止定时器
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // 处理剩余数据
      if (pcmDataRef.current.length > 0) {
        const totalLength = pcmDataRef.current.reduce((sum, arr) => sum + arr.length, 0);
        const mergedData = new Float32Array(totalLength);
        let offset = 0;
        
        for (const chunk of pcmDataRef.current) {
          mergedData.set(chunk, offset);
          offset += chunk.length;
        }
        
        const pcmBuffer = convertFloat32ToPCM16(mergedData);
        
        if (onStop) {
          onStop(pcmBuffer);
        }
      }
      
      // 清理音频节点
      if (processorNodeRef.current) {
        processorNodeRef.current.disconnect();
        processorNodeRef.current = null;
      }
      
      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
      }
      
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      
      // 停止媒体流
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      setStatus('stopped');
      pcmDataRef.current = [];
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMessage));
      }
    }
  }, [onStop, onError]);

  const cleanup = useCallback(() => {
    if (status === 'recording') {
      stopRecording();
    }
  }, [status, stopRecording]);

  return {
    status,
    error,
    startRecording,
    stopRecording,
    cleanup
  };
};